import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { OrganizationService } from '../services/organization.service';
import { UpdateOrganizationDto } from '../types/organization.types';

// Query key for organization data
export const getOrganizationQueryKey = () => ['organization'];

/**
 * Hook for managing organization operations
 */
export const useOrganization = () => {
  const queryClient = useQueryClient();

  // Fetch organization data
  const { data: organization, isLoading, error } = useQuery({
    queryKey: getOrganizationQueryKey(),
    queryFn: () => OrganizationService.getOrganization(),
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Update organization mutation
  const { mutate: updateOrganization, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdateOrganizationDto) => OrganizationService.updateOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getOrganizationQueryKey() });
      toast.success('Organization information updated successfully');
    },
    onError: () => {
      toast.error('Failed to update organization information');
    },
  });

  return {
    organization,
    isLoading,
    error,
    updateOrganization,
    isUpdating,
  };
};
